import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { BrandCard } from "@/components/public/BrandCard";
import { ClothingItemCard } from "@/components/public/ClothingItemCard";
import { AdSlot } from "@/components/public/AdSlot";
import { MarkdownRenderer } from "@/components/public/MarkdownRenderer";
import { Leaf, AlertTriangle, XCircle, ArrowRight } from "lucide-react";
import { SeoHead } from "@/components/public/SeoHead";
import { JsonLd } from "@/components/public/JsonLd";

export default function HomePage() {
  const { data: home, isLoading } = useQuery({
    queryKey: ["home"],
    queryFn: async () => (await api.get("/public/home")).data,
  });
  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: async () => (await api.get("/public/settings")).data,
  });
  // Track image-load failures so the hero collapses to a single full-width
  // column if the URL is broken (broken-image icon is never shown).
  const [imageBroken, setImageBroken] = useState(false);
  const eyebrow = settings?.hero_eyebrow || "Curated sustainable fashion directory";
  const heroImage = (settings?.hero_image_url || "").trim();
  const showHeroImage = Boolean(heroImage) && !imageBroken;
  const tagline = settings?.tagline || "Find clothing that actually meets *sustainable* material standards.";
  const subtitle = settings?.hero_subtitle || "Sustainably Curated reviews clothing brands and materials so you can quickly discover fashion made from approved sustainable, organic, and biodegradable materials.";
  // Render *word* as italic forest accent.
  const taglineParts = tagline.split(/(\*[^*]+\*)/g);

  const origin = typeof window !== "undefined" ? window.location.origin : "https://sustainablycurated.com";
  const socials = (settings?.social_links || []).map((s) => s.url).filter(Boolean);
  const homeJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Sustainably Curated",
      url: origin,
      description: subtitle,
      potentialAction: {
        "@type": "SearchAction",
        target: `${origin}/clothing?search={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Sustainably Curated",
      url: origin,
      logo: `${origin}/leaf.svg`,
      description: subtitle,
      foundingDate: "2026",
      founder: { "@type": "Person", name: "Teresa Cordeiro" },
      sameAs: socials.length ? socials : ["https://www.instagram.com/teresarcordeiro", "https://www.tiktok.com/@the.cool.girl.cut"],
    },
  ];

  return (
    <div className="font-sans">
      <SeoHead title="Sustainably Curated — Curated Sustainable Fashion Directory" path="/" />
      <JsonLd data={homeJsonLd} />
      {/* HERO */}
      <section className="relative overflow-hidden bg-ivory" data-testid="home-hero">
        <div className={`pt-0 pr-8 pb-0 pl-8 grid gap-0 items-stretch ${showHeroImage ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}>
          <div className="space-y-6 animate-fade-up lg:pt-4 pb-6">
            <span className="inline-block text-xs uppercase tracking-[0.25em] text-forest" data-testid="hero-eyebrow">{eyebrow}</span>
            <h1 className="editorial-headline text-4xl sm:text-5xl lg:text-6xl text-ink !mt-5 !leading-[1.25]" data-testid="hero-tagline">
              {taglineParts.map((part, i) =>
                part.startsWith("*") && part.endsWith("*") && part.length > 2
                  ? <em key={i} className="italic text-forest">{part.slice(1, -1)}</em>
                  : <span key={i}>{part}</span>
              )}
            </h1>
            <p className={`text-lg text-muted_text ${showHeroImage ? "max-w-xl" : "max-w-3xl"}`} data-testid="hero-subtitle">
              {subtitle}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/clothing" className="inline-flex items-center gap-2 bg-forest text-ivory rounded-full px-6 py-3 hover:bg-forest/90 transition-colors" data-testid="hero-cta-clothing">
                Browse clothing <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/brands" className="inline-flex items-center bg-champagne text-forest rounded-full px-6 py-3 hover:bg-champagne/80 transition-colors" data-testid="hero-cta-brands">
                Browse brands
              </Link>
              <Link to="/methodology" className="inline-flex items-center text-sm text-forest underline underline-offset-4 ml-2" data-testid="hero-cta-methodology">
                Read our Methodology →
              </Link>
            </div>
          </div>
          {showHeroImage && (
            <div
              className="relative aspect-[4/5] lg:aspect-auto lg:h-full lg:min-h-0 rounded-3xl overflow-hidden grain-overlay"
              style={{
                WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 35%)",
                maskImage: "linear-gradient(to right, transparent 0%, black 35%)",
              }}
            >
              <img
                src={heroImage}
                alt="Editorial sustainable fashion"
                className="absolute inset-0 w-full h-full object-cover"
                onError={() => setImageBroken(true)}
              />
            </div>
          )}
        </div>
      </section>

      {/* Why */}
      <section className="bg-white border-y border-line" data-testid="home-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
          <div className="grid md:grid-cols-3 gap-x-8 gap-y-4">
            <Why
              icon={Leaf}
              title={settings?.pillar_1_title || "Material-first reviews"}
              body={settings?.pillar_1_body || "Every clothing item is classified by composition: GREEN, YELLOW, or RED. Synthetic content is capped at 5% and individually reviewed."}
            />
            <Why
              icon={AlertTriangle}
              title={settings?.pillar_2_title || "Transparent scoring"}
              body={settings?.pillar_2_body || "Brand scores show, plainly, what fraction of reviewed items meet our standards."}
            />
            <Why
              icon={XCircle}
              title={settings?.pillar_3_title || "No vague claims"}
              body={settings?.pillar_3_body || "We don't accept marketing copy as proof. Composition labels are what count."}
            />
          </div>
          {settings?.pillars_footer && (
            <div className="mt-8 text-center" data-testid="home-pillars-footer">
              <MarkdownRenderer source={settings.pillars_footer} className="is-pillars-footer" />
            </div>
          )}
        </div>
      </section>

      <AdSlot slotKey="home_mid" />

      {/* Featured brands — only render when at least one brand is marked featured in admin */}
      {(home?.featured_brands?.length > 0 || isLoading) && (
        <div className="border-t border-line">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8" data-testid="home-featured-brands">
          <div className="flex items-end justify-between mb-6">
            <h2 className="editorial-headline text-2xl sm:text-3xl">Featured brands</h2>
            <Link to="/brands" className="text-sm text-forest underline underline-offset-4">All brands →</Link>
          </div>
          {/*
            Match the visual size of the Brands list cards. The /brands page
            renders into ~808px (max-w-7xl minus the w-64 sidebar) so cards land
            at ~200px wide. Home has no sidebar, so we'd otherwise get cards
            ~310px wide. Bumping to lg:grid-cols-5 xl:grid-cols-6 brings them
            to ~213px / ~196px wide, which matches Brands list cadence and
            also lets all 6 featured brands sit in a single row.
          */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-5">
            {(home?.featured_brands || []).map((b) => (
              <BrandCard key={b.id} brand={b} />
            ))}
            {isLoading && Array(6).fill(0).map((_, i) => <div key={i} className="h-64 rounded-2xl bg-line/40 animate-pulse" />)}
          </div>
        </section>
        </div>
      )}

      {/* Featured items */}
      <section className="bg-white border-y border-line" data-testid="home-featured-items">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
          <div className="flex items-end justify-between mb-6">
            <h2 className="editorial-headline text-2xl sm:text-3xl">Recently approved items</h2>
            <Link to="/clothing" className="text-sm text-forest underline underline-offset-4">All clothing →</Link>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {(home?.featured_items || []).map((it) => (
              <ClothingItemCard key={it.id} item={it} />
            ))}
          </div>
        </div>
      </section>

      <AdSlot slotKey="home_lower" />

    </div>
  );
}

function Why({ icon: Icon, title, body }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-6 h-6 text-forest shrink-0" />
        <h3 className="font-serif text-2xl">{title}</h3>
      </div>
      <MarkdownRenderer source={body} className="is-compact" />
    </div>
  );
}
